/**
 * This module contains the lat long, MGRS and NOTAM geocoders that allow the user to quickly
 * zoom to a coordinate.
 */
import * as mgrs from 'mgrs';
// import { Result } from 'ngx-mapbox-gl/lib/control/geocoder-control.directive';

function coordinateResult(
    id: string,
    name: string,
    latitude: number,
    longitude: number
): any {
    return {
        type: 'Feature',
        text: name,
        place_type: ['coordinate'],
        properties: {},
        bbox: null,
        place_name: name,
        id: id,
        center: [longitude, latitude],
        geometry: {
            type: 'Point',
            coordinates: [longitude, latitude]
        },
        relevance: null,
        address: null,
        context: null
    };
}

function makeLatLngName(latitude: number, longitude: number) {
    return `Lat: ${latitude} Long: ${longitude}`;
}

function latLongResult(id: string, coord1: number, coord2: number) {
    const geocodes = [];

    if (coord1 < -90 || coord1 > 90) {
        // must be lng, lat
        geocodes.push(
            coordinateResult(id, makeLatLngName(coord2, coord1), coord2, coord1)
        );
    }

    if (coord2 < -90 || coord2 > 90) {
        // must be lat, lng
        geocodes.push(
            coordinateResult(id, makeLatLngName(coord1, coord2), coord1, coord2)
        );
    }

    if (geocodes.length === 0) {
        // else could be either lng, lat or lat, lng
        geocodes.push(
            coordinateResult(id, makeLatLngName(coord1, coord2), coord1, coord2)
        );
        geocodes.push(
            coordinateResult(id, makeLatLngName(coord2, coord1), coord2, coord1)
        );
    }

    return geocodes;
}

/**
 * NOTAMs express coordinates as degrees, minutes and optionally seconds, with the hemisphere
 * as a trailing letter, e.g. `172637S 1482200E`, `1726S 14822E` or, with decimal seconds,
 * `271552.0S 1521230.4E`.
 */
const NOTAM_PATTERN =
    /^[ ]*(?:NOTAM:? *)?(\d{2})(\d{2})(\d{2}(?:\.\d+)?)?[ ]*([NS])[, ]*(\d{3})(\d{2})(\d{2}(?:\.\d+)?)?[ ]*([EW])[ ]*$/i;

/** Round to a sub-metre precision, which avoids floating point noise in the result. */
function roundCoordinate(value: number) {
    return Math.round(value * 1e6) / 1e6;
}

/**
 * Convert degrees, minutes and seconds into decimal degrees, returning undefined if any of the
 * parts are out of range.
 */
function dmsToDecimal(
    degrees: number,
    minutes: number,
    seconds: number,
    limit: number
) {
    if (minutes >= 60 || seconds >= 60) {
        return undefined;
    }
    const decimal = degrees + minutes / 60 + seconds / 3600;
    if (decimal > limit) {
        return undefined;
    }
    return roundCoordinate(decimal);
}

function notamResult(query: string): any[] {
    const match = query.match(NOTAM_PATTERN);
    if (match == null) {
        return [];
    }

    const [
        ,
        latDegrees,
        latMinutes,
        latSeconds,
        latHemisphere,
        lngDegrees,
        lngMinutes,
        lngSeconds,
        lngHemisphere
    ] = match;

    const latitude = dmsToDecimal(
        Number(latDegrees),
        Number(latMinutes),
        Number(latSeconds ?? 0),
        90
    );
    const longitude = dmsToDecimal(
        Number(lngDegrees),
        Number(lngMinutes),
        Number(lngSeconds ?? 0),
        180
    );
    if (latitude == null || longitude == null) {
        return [];
    }

    const latText = `${latDegrees}${latMinutes}${
        latSeconds ?? ''
    }${latHemisphere.toUpperCase()}`;
    const lngText = `${lngDegrees}${lngMinutes}${
        lngSeconds ?? ''
    }${lngHemisphere.toUpperCase()}`;

    return [
        coordinateResult(
            `notam-${latText}-${lngText}`,
            `NOTAM: ${latText} ${lngText}`,
            latHemisphere.toUpperCase() === 'S' ? -latitude : latitude,
            lngHemisphere.toUpperCase() === 'W' ? -longitude : longitude
        )
    ];
}

export function spacialGeocoder(query: string): any[] {
    const notamGeocodes = notamResult(query);
    if (notamGeocodes.length > 0) {
        return notamGeocodes;
    }
    const latLongMatch = query.match(
        /^[ ]*(?:Lat: )?(-?\d+\.?\d*)[, ]+(?:Lng: )?(-?\d+\.?\d*)[ ]*$/i
    );
    if (latLongMatch != null && latLongMatch.length > 0) {
        const coord1 = Number(latLongMatch[1]);
        const coord2 = Number(latLongMatch[2]);
        const id = `latlng-${latLongMatch[1]}-${latLongMatch[2]}`;
        return latLongResult(id, coord1, coord2);
    }
    try {
        const mgrsMatch = query.match(/^[ ]*(?:MGRS:? )?([A-Za-z0-9]+)[ ]*$/i);
        if (mgrsMatch != null && mgrsMatch.length > 0) {
            const mgrsQuery = mgrsMatch[1].toUpperCase();
            const [lng, lat] = mgrs.inverse(mgrsQuery);
            return [
                coordinateResult(
                    `mgrs-${mgrsQuery}`,
                    `MGRS: ${mgrsQuery}`,
                    lat,
                    lng
                )
            ];
        }
    } catch {
        // do-nothing
    }
    return [];
}
